import { Box, type BoxRef, type BoxStyles } from '../../../mads';
import { LinearGradient } from '../LinearGradient/linear-gradient';

import React, { PureComponent, useEffect, forwardRef } from 'react';
import {
    Animated,
    Platform,
    StyleSheet,
    type StyleProp,
    type ViewStyle,
    type ViewProps,
} from 'react-native';

type LinearGradientComponent = typeof LinearGradient | typeof Box;

interface ShimmerPlaceholderProps {
    /** Width of the shimmer placeholder */
    width?: number;
    /** Height of the shimmer placeholder */
    height?: number;
    /** Colors for the shimmer gradient */
    shimmerColors?: [string, string, string];
    /** Whether the shimmer animation should run in reverse */
    isReversed?: boolean;
    /** Stop the shimmer animation from auto-running */
    stopAutoRun?: boolean;
    /** When true, shows the children content instead of shimmer */
    visible?: boolean;
    /** Gradient color stop locations (values between 0 and 1) */
    location?: [number, number, number];
    /** Style for the outer container */
    style?: StyleProp<ViewStyle>;
    /** Style for the content container when visible */
    contentStyle?: StyleProp<ViewStyle>;
    /** Style for the shimmer container when not visible */
    shimmerStyle?: StyleProp<ViewStyle>;
    /** Custom LinearGradient component */
    LinearGradient?: LinearGradientComponent;
    /** Content to show when visible is true */
    children?: React.ReactNode;
    /** Delay before starting the animation (ms) */
    delay?: number;
    /** Duration of one shimmer cycle (ms) */
    duration?: number;
    /** Whether animation should report to InteractionManager */
    isInteraction?: boolean;
    /** Width of shimmer as a percentage of total width (0-1) */
    shimmerWidthPercent?: number;
    /** Props passed to the outer container View */
    containerProps?: ViewProps;
    /** Props passed to the shimmer container View */
    shimmerContainerProps?: ViewProps;
    /** Props passed to the children container View */
    childrenContainerProps?: ViewProps;
}

interface BasedShimmerPlaceHolderProps extends ShimmerPlaceholderProps {
    animatedValue: Animated.CompositeAnimation;
    beginShimmerPosition: Animated.Value;
}

const getOutputRange = (width: number, isReversed: boolean): [number, number] =>
    isReversed ? [width, -width] : [-width, width];

const BasedShimmerPlaceHolder: React.FC<BasedShimmerPlaceHolderProps> = (props) => {
    const {
        width = 200,
        height = 15,
        shimmerColors = ['#ebebeb', '#c5c5c5', '#ebebeb'],
        isReversed = false,
        stopAutoRun = false,
        visible,
        location = [0.3, 0.5, 0.7],
        style,
        contentStyle,
        shimmerStyle,
        LinearGradient: LinearGradientComponent = Box,
        children,
        animatedValue,
        beginShimmerPosition,
        shimmerWidthPercent = 1,
        containerProps,
        shimmerContainerProps,
        childrenContainerProps,
    } = props;

    const linearTranslate = beginShimmerPosition.interpolate({
        inputRange: [-1, 1],
        outputRange: getOutputRange(width, isReversed),
    });

    useEffect(() => {
        if (!stopAutoRun) {
            animatedValue.start();
        }
        return () => {
            animatedValue.stop();
        };
    }, [stopAutoRun, animatedValue]);

    useEffect(() => {
        if (visible) {
            animatedValue.stop();
        }
        if (!visible && !stopAutoRun) {
            animatedValue.start();
        }
    }, [visible, stopAutoRun, animatedValue]);

    const gradientSurfaceStyle = { flex: 1, width: width * shimmerWidthPercent };

    return (
        <Box
            sx={
                [
                    !visible && { height, width },
                    styles.container,
                    !visible && shimmerStyle,
                    style,
                ] as BoxStyles
            }
            {...containerProps}
        >
            {/* Force render children to restrict rendering twice */}
            <Box
                sx={
                    [!visible && { width: 0, height: 0, opacity: 0 }, visible && contentStyle] as BoxStyles
                }
                {...childrenContainerProps}
            >
                {children}
            </Box>
            {!visible && (
                <Box
                    sx={{ flex: 1, backgroundColor: shimmerColors[0] }}
                    {...shimmerContainerProps}
                >
                    <Animated.View
                        style={{ flex: 1, transform: [{ translateX: linearTranslate }] }}
                    >
                        {LinearGradientComponent === Box ? (
                            <Box sx={gradientSurfaceStyle} />
                        ) : (
                            <LinearGradientComponent
                                colors={shimmerColors}
                                style={gradientSurfaceStyle}
                                start={{
                                    x: -1,
                                    y: 0.5,
                                }}
                                end={{
                                    x: 2,
                                    y: 0.5,
                                }}
                                locations={location}
                            />
                        )}
                    </Animated.View>
                </Box>
            )}
        </Box>
    );
};

interface ShimmerPlaceHolderState {
    beginShimmerPosition: Animated.Value;
}

class ShimmerPlaceHolder extends PureComponent<ShimmerPlaceholderProps, ShimmerPlaceHolderState> {
    static defaultProps: Partial<ShimmerPlaceholderProps> = {
        delay: 0,
        duration: 1000,
        isInteraction: true,
    };

    state: ShimmerPlaceHolderState = {
        beginShimmerPosition: new Animated.Value(-1),
    };

    getAnimated = (): Animated.CompositeAnimation => {
        const { delay = 0, duration = 1000, isInteraction = true } = this.props;
        return Animated.loop(
            Animated.timing(this.state.beginShimmerPosition, {
                toValue: 1,
                delay,
                duration,
                useNativeDriver: Platform.OS !== 'web',
                isInteraction,
            }),
        );
    };

    animatedValue = this.getAnimated();

    render() {
        return (
            <BasedShimmerPlaceHolder
                {...this.props}
                animatedValue={this.animatedValue}
                beginShimmerPosition={this.state.beginShimmerPosition}
            />
        );
    }
}

const styles = StyleSheet.create({
    container: {
        overflow: 'hidden',
    },
});

/**
 * Creates a ShimmerPlaceHolder component with a custom LinearGradient component.
 * Use this factory function to create a shimmer placeholder that uses expo-linear-gradient
 * or any other gradient library.
 *
 * @example
 * ```tsx
 * import { LinearGradient } from 'expo-linear-gradient';
 * const ShimmerPlaceHolder = createShimmerPlaceHolder(LinearGradient);
 *
 * <ShimmerPlaceHolder visible={isLoading} width={200} height={20} />
 * ```
 */
export const createShimmerPlaceHolder = (LinearGradientComponent: LinearGradientComponent = Box) =>
    forwardRef<BoxRef, ShimmerPlaceholderProps>((props, ref) => (
        <Box ref={ref}>
            <ShimmerPlaceHolder LinearGradient={LinearGradientComponent} {...props} />
        </Box>
    ));

/**
 * Pre-configured ShimmerPlaceHolder using expo-linear-gradient.
 * This is the recommended component to use directly.
 *
 * @example
 * ```tsx
 * <ShimmerPlaceHolderWithGradient visible={isLoading} width={200} height={20} />
 * ```
 */
export const ShimmerPlaceHolderWithGradient = createShimmerPlaceHolder(LinearGradient);

export default ShimmerPlaceHolder;
export type { ShimmerPlaceholderProps };
